import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dialog, DialogBody, DialogFooter, Spinner } from '@blueprintjs/core';
import { fetchVerificationRun, type VerificationCheck } from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { ErrorNote, Pill, VERIFY_LABEL, VerifyCell, fmtInt, fmtWhen } from './ui';

type Kind = 'phone' | 'email';
type Group = 'good' | 'attention' | 'waiting' | 'skipped';

/** Which filter chip a check counts under. */
function groupOf(c: VerificationCheck): Group {
  if (c.how === 'skipped') return 'skipped';
  if (c.state === 'queued' || c.state === 'sent') return 'waiting';
  if (c.state === 'recorded' && ['success', 'info'].includes(VERIFY_LABEL[c.result ?? '']?.tone ?? '')) return 'good';
  return 'attention';
}

const checksOf = (l: { checks: Partial<Record<Kind, VerificationCheck>> }) =>
  Object.values(l.checks).filter((c): c is VerificationCheck => !!c);

const GROUPS: { id: Group | 'all'; label: string }[] = [
  { id: 'all', label: 'All' }, { id: 'good', label: 'Good' }, { id: 'attention', label: 'Needs a look' },
  { id: 'waiting', label: 'Waiting' }, { id: 'skipped', label: 'Skipped' },
];

function HowNote({ check }: { check: VerificationCheck }) {
  const how = check.how === 'checked' ? 'New check'
    : check.how === 'joined' ? 'Shared another run\'s check'
    : check.how === 'stored' ? `Earlier answer, ${fmtWhen(check.at)}`
    : check.skip;
  return <span className="li-vcheck__note">{[how, check.detail].filter(Boolean).join('. ')}</span>;
}

function CheckCell({ value, check }: { value: string | null; check: VerificationCheck | undefined }) {
  if (!check) return <span className="li-muted">-</span>;
  return (
    <div className="li-vcheck">
      {value ? <span className="li-vcheck__value">{value}</span> : null}
      <span className="li-vcheck__result">
        {check.how === 'skipped' ? null
          : check.state === 'recorded' ? <VerifyCell status={check.result} />
          : check.state === 'failed' ? <Pill tone="danger" title={check.error ?? undefined}>Failed</Pill>
          : check.state === 'outcome_unknown' ? <Pill tone="warn" title="The provider was called but no answer came back. It is not sent again.">No answer</Pill>
          : <Pill tone="info">Waiting</Pill>}
        <HowNote check={check} />
      </span>
    </div>
  );
}

/** One verification run, lead by lead: what was checked, the answer and where it came from. */
export default function VerificationRunDialog({ runId, onClose, onOpenLead }: {
  runId: number; onClose: () => void; onOpenLead: (id: number) => void;
}) {
  const q = useQuery({ queryKey: leadImportKeys.verificationRun(runId), queryFn: () => fetchVerificationRun(runId), ...queryPolicies.recordDetail });
  const [group, setGroup] = useState<Group | 'all'>('all');
  const run = q.data?.run;
  const kinds: Kind[] = run?.kinds ?? [];
  const leads = useMemo(() => q.data?.leads ?? [], [q.data]);
  const counts = useMemo(() => {
    const n: Record<Group, number> = { good: 0, attention: 0, waiting: 0, skipped: 0 };
    for (const l of leads) {
      const groups = new Set(checksOf(l).map(groupOf));
      groups.forEach((g) => { n[g] += 1; });
    }
    return n;
  }, [leads]);
  const shown = group === 'all' ? leads : leads.filter((l) => checksOf(l).some((c) => groupOf(c) === group));

  return (
    <Dialog isOpen onClose={onClose} title={`Verification run #${runId}`} style={{ width: 'min(920px, 100vw)' }}>
      <DialogBody>
        {q.isLoading && <Spinner size={20} />}
        <ErrorNote error={q.error} />
        {run && (
          <div className="li-stack">
            <p className="li-vrun__summary">
              {fmtInt(run.lead_count)} {run.lead_count === 1 ? 'lead' : 'leads'}, {kinds.map((k) => (k === 'phone' ? 'phones' : 'emails')).join(' and ')} checked.
              {' '}Started {fmtWhen(run.created_at)}{run.requested_by ? ` by ${run.requested_by}` : ''}.
              {run.credits_used != null && <> {fmtInt(run.credits_used)} provider {Number(run.credits_used) === 1 ? 'credit' : 'credits'} used.</>}
              {run.force && <> <Pill tone="muted">Checked again</Pill></>}
            </p>
            <div className="li-chips" role="group" aria-label="Show leads">
              {GROUPS.filter((g) => g.id === 'all' || counts[g.id] > 0).map((g) => (
                <button key={g.id} type="button" className={`li-chip${group === g.id ? ' is-on' : ''}`} aria-pressed={group === g.id} onClick={() => setGroup(g.id)}>
                  <b>{fmtInt(g.id === 'all' ? leads.length : counts[g.id])}</b> {g.label}
                </button>
              ))}
            </div>
            <div className="li-tablewrap" style={{ maxHeight: '60vh' }}>
              <table className="li-table li-table--compact">
                <thead><tr><th>Lead</th>{kinds.includes('phone') && <th>Phone</th>}{kinds.includes('email') && <th>Email</th>}</tr></thead>
                <tbody>
                  {shown.map((l) => {
                    const name = [l.first_name, l.last_name].filter(Boolean).join(' ');
                    return (
                      <tr key={l.id} className="is-clickable" tabIndex={0} title="Open the lead"
                        onClick={() => onOpenLead(l.id)} onKeyDown={(e) => { if (e.key === 'Enter') onOpenLead(l.id); }}>
                        <td>
                          <b>{l.business_name || name || `Lead ${l.id}`}</b>
                          {l.business_name && name && <div className="li-muted">{name}</div>}
                        </td>
                        {kinds.includes('phone') && <td><CheckCell value={l.phone} check={l.checks.phone} /></td>}
                        {kinds.includes('email') && <td><CheckCell value={l.email} check={l.checks.email} /></td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogBody>
      <DialogFooter actions={<Button onClick={onClose}>Close</Button>} />
    </Dialog>
  );
}
